import axios from './axiosConfig';
import { Attachment } from '../../types/chat';
import s3UploadService from './s3UploadService';

/**
 * ファイルをアップロードする
 * @param file アップロードするファイル
 * @param chatId チャットID
 * @param messageId メッセージID
 * @returns アップロードしたファイルの情報
 */
export const uploadAttachment = async (
  file: File,
  chatId: number,
  messageId: number
): Promise<Attachment> => {
  try {
    // 画像ファイルの場合はS3にもアップロード
    if (file.type.startsWith('image/')) {
      console.log('🖼️ 画像をS3にアップロード:', file.name);
      const s3Result = await s3UploadService.uploadImageToS3(file);
      if (s3Result) {
        console.log('✅ S3アップロード成功:', {
          filename: s3Result.filename,
          s3_url: s3Result.s3_url,
          s3_key: s3Result.s3_key
        });
      } else {
        console.warn('⚠️ S3アップロードをスキップ（画像形式エラーまたはアップロード失敗）');
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('chat_id', chatId.toString());
    formData.append('message_id', messageId.toString());

    const response = await axios.post('/chats/attachments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        // プログレスバーなどの実装はここで行う
        // const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${progressEvent.loaded} bytes`);
      },
    });

    return response.data.attachment;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

/**
 * 添付ファイルを取得する
 * @param attachmentId 添付ファイルID
 * @returns 添付ファイルの情報
 */
export const getAttachment = async (attachmentId: number): Promise<Attachment> => {
  try {
    const response = await axios.get(`/chats/attachments/${attachmentId}`);
    return response.data.attachment;
  } catch (error) {
    console.error('Failed to get attachment:', error);
    throw error;
  }
};

/**
 * チャットに関連する全ての添付ファイルを取得する
 * @param chatId チャットID
 * @returns 添付ファイル情報の配列
 */
export const getAttachmentsByChatId = async (chatId: number): Promise<Attachment[]> => {
  try {
    const response = await axios.get(`/chats/attachments/chat/${chatId}`);
    return response.data.attachments;
  } catch (error) {
    console.error('Failed to get chat attachments:', error);
    throw error;
  }
};

/**
 * 特定のメッセージに関連する添付ファイルを取得する
 * @param chatId チャットID
 * @param messageId メッセージID
 * @returns 添付ファイル情報の配列
 */
export const getAttachmentsByMessageId = async (
  chatId: number,
  messageId: number
): Promise<Attachment[]> => {
  try {
    const response = await axios.get(`/chats/attachments/chat/${chatId}/message/${messageId}`);
    return response.data.attachments;
  } catch (error) {
    console.error('Failed to get message attachments:', error);
    throw error;
  }
};

/**
 * 添付ファイルを削除する
 * @param attachmentId 削除する添付ファイルのID
 * @returns 削除結果
 */
export const deleteAttachment = async (attachmentId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(`/chats/attachments/${attachmentId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete attachment:', error);
    throw error;
  }
};

/**
 * ファイルがアップロード可能かをチェックする
 * @param file チェックするファイル
 * @returns {boolean} アップロード可能かどうか
 */
export const isValidFileType = (file: File): boolean => {
  // 許可されるMIMEタイプ
  const validMimeTypes = [
    // 画像
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/bmp',
    // 動画
    'video/mp4',
    'video/mpeg',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-ms-wmv',
    'video/webm',
    // Excel
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    // PowerPoint
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.presentation',
    // PDF
    'application/pdf',
    // Word
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/rtf'
  ];
  
  // MIMEタイプで判定
  if (validMimeTypes.includes(file.type)) {
    return true;
  }
  
  // ファイル拡張子でも判定
  const extension = file.name.split('.').pop()?.toLowerCase();
  const validExtensions = [
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', // 画像
    'mp4', 'mpeg', 'mpg', 'mov', 'avi', 'wmv', 'webm', // 動画
    'xlsx', 'xls', 'csv', // Excel
    'pptx', 'ppt', 'odp', // PowerPoint
    'pdf', // PDF
    'doc', 'docx', 'txt', 'rtf' // Word
  ];
  
  return validExtensions.includes(extension || '');
};

/**
 * ファイルサイズが制限内かをチェックする
 * @param file チェックするファイル
 * @param maxSizeInBytes 最大サイズ（バイト単位）
 * @returns {boolean} サイズが制限内かどうか
 */
export const isValidFileSize = (file: File, maxSizeInBytes: number = 1024 * 1024 * 1024): boolean => {
  return file.size <= maxSizeInBytes; // デフォルト1GB
};

/**
 * ファイルサイズを人間が読みやすい形式に変換する
 * @param bytes ファイルサイズ（バイト単位）
 * @returns 人間が読みやすい形式のサイズ
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
};
